import type {
  Invoice,
  InvoiceListResponse,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  InvoiceStatus,
  InvoiceFilter,
} from '../types/invoice';
import { get, post, put, patch, del, buildUrl } from './apiClient';

const API_BASE = '/api/v1/invoices';

// ============================================================================
// Invoice CRUD
// ============================================================================

export async function createInvoice(request: CreateInvoiceRequest): Promise<Invoice> {
  return post<Invoice>(API_BASE, request);
}

export async function getInvoice(id: string): Promise<Invoice> {
  return get<Invoice>(`${API_BASE}/${id}`);
}

export async function listInvoices(filter?: InvoiceFilter): Promise<InvoiceListResponse> {
  return get<InvoiceListResponse>(buildUrl(API_BASE, filter as Record<string, unknown> | undefined));
}

export async function updateInvoice(id: string, request: UpdateInvoiceRequest): Promise<Invoice> {
  return put<Invoice>(`${API_BASE}/${id}`, request);
}

// ============================================================================
// Status Management
// ============================================================================

export async function updateInvoiceStatus(id: string, status: InvoiceStatus, paidDate?: string): Promise<void> {
  await patch(`${API_BASE}/${id}/status`, { status, paid_date: paidDate });
}

// ============================================================================
// Delete
// ============================================================================

export async function deleteInvoice(id: string): Promise<void> {
  await del(`${API_BASE}/${id}`);
}

// ============================================================================
// Utility API
// ============================================================================

export async function getNextInvoiceNumber(): Promise<string> {
  const data = await get<{ invoice_number: string }>(`${API_BASE}/next-number`);
  return data.invoice_number;
}

// ============================================================================
// Invoice Detection & Extraction (AI-powered)
// ============================================================================

export interface InvoiceDetectionRequest {
  file_base64: string;
  mime_type: string;
  folder_hint?: string;
}

export interface InvoiceDetectionResult {
  // Existing fields (kept for backward compat with inline detection)
  is_invoice: boolean;
  invoice_type: 'purchase' | 'sales' | 'unknown';
  confidence: number;
  detected_language: string;
  reasoning: string;
  hints: {
    has_tax_id?: boolean;
    has_line_items?: boolean;
    has_total?: boolean;
    counterparty_name?: string;
    invoice_number_detected?: string;
    date_detected?: string;
  };

  // Enhanced fields (from Cloud Run detection)
  document_type?: string;           // 三聯式, 二聯式, 電子發票_三聯式, etc.
  document_type_label?: string;     // Human-readable label
  electronic?: boolean;
  copy_type?: string;               // 存根聯, 扣抵聯, 收執聯
  copy_type_label?: string;
  copy_color?: string;              // red, green, blue, none
  format?: string;                  // thermal, a4, preprinted_form, etc.
  is_handwritten?: boolean;
  has_buyer_unified_number?: boolean;
  has_seller_unified_number?: boolean;
  business_binding?: {
    direction: string;
    process: string[];
    retention_years: number;
    description: string;
  };
  metadata?: Record<string, { value: unknown; bbox?: { x: number; y: number; w: number; h: number } }>;
  page_image?: string;              // Rendered page PNG as base64 (from Cloud Run preprocessing)
  page_image_mime?: string;         // "image/png"
  classification_detail?: {
    reasoning: string;
    passes: Array<{ pass: string; result: string; confidence: number }>;
  };
}

export interface InvoiceExtractionRequest {
  file_base64: string;
  mime_type: string;
  invoice_type: 'purchase' | 'sales';
  detected_language?: string;
  detection_context?: Record<string, unknown>;  // Full Cloud Run detection output
}

export interface ExtractedCounterparty {
  name: string | null;
  tax_id: string | null;
  address: string | null;
  contact: string | null;
}

export interface ExtractedLineItem {
  line_no: number;
  product_name: string;
  specification: string;
  quantity: number;
  unit_price: number;
  amount: number;
  currency: string;
}

export interface ExtractedInvoiceData {
  invoice_number: string | null;
  invoice_date: string | null;
  due_date: string | null;
  counterparty: ExtractedCounterparty;
  items: ExtractedLineItem[];
  subtotal: number | null;
  tax_rate: number | null;
  tax_amount: number | null;
  grand_total: number | null;
  currency: string;
  payment_terms: string | null;
  order_reference: string | null;
  notes: string | null;
}

export interface InvoiceExtractionResult {
  extracted: ExtractedInvoiceData;
  confidence: Record<string, string>;
}

// Preprocess: PDF→PNG + text extraction + regex field detection (no AI, instant)
export interface InvoicePreprocessRequest {
  file_base64: string;
  mime_type: string;
}

export interface InvoicePreprocessResult {
  page_image: string;           // Rendered PNG base64
  page_image_mime: string;      // "image/png"
  page_dimensions: { width: number; height: number };
  extracted_text: string;
  format_hint: string;
  page_count: number;
  metadata: Record<string, { value: unknown; bbox?: { x: number; y: number; w: number; h: number } }>;
}

export async function preprocessInvoice(request: InvoicePreprocessRequest): Promise<InvoicePreprocessResult> {
  return post<InvoicePreprocessResult>(`${API_BASE}/preprocess`, request);
}

// OCR: Tesseract OCR on a cropped image region (no AI, local processing)
export interface InvoiceOCRRequest {
  image_base64: string;       // Cropped region PNG as base64
  lang?: string;              // Tesseract language, default "chi_tra+eng"
  enhance?: boolean;          // Preprocessing (contrast, sharpen, upscale), default true
  region_bbox?: { x: number; y: number; w: number; h: number };  // Region position in page coords
  page_dimensions?: { width: number; height: number };
}

export interface InvoiceOCRWord {
  text: string;
  confidence: number;
  bbox: { x: number; y: number; w: number; h: number };        // relative to cropped image
  bbox_page: { x: number; y: number; w: number; h: number };   // relative to full page
}

export interface InvoiceOCRResult {
  text: string;
  words: InvoiceOCRWord[];
  word_count: number;
  image_size: { width: number; height: number };
  lang: string;
}

export async function ocrInvoiceRegion(request: InvoiceOCRRequest): Promise<InvoiceOCRResult> {
  return post<InvoiceOCRResult>(`${API_BASE}/ocr`, request);
}

// Company lookup via Taiwan company registry (opendata.vip)
export interface CompanyLookupResult {
  found: boolean;
  company: {
    unified_number: string;
    company_name: string;
    status: string;
    capital: number;
    responsible_person: string;
    address: string;
    setup_date: string;
  } | null;
}

export async function lookupCompany(keyword: string): Promise<CompanyLookupResult> {
  return post<CompanyLookupResult>(`${API_BASE}/lookup`, { keyword });
}

// Detect: Claude Vision classification + metadata with bboxes (AI call)
export async function detectInvoiceType(request: InvoiceDetectionRequest): Promise<InvoiceDetectionResult> {
  return post<InvoiceDetectionResult>(`${API_BASE}/detect`, request);
}

// Extract: Enhanced Claude extraction with detection context (AI call)
export async function extractInvoiceData(request: InvoiceExtractionRequest): Promise<InvoiceExtractionResult> {
  return post<InvoiceExtractionResult>(`${API_BASE}/extract`, request);
}

// ============================================================================
// Batch Invoice Processing
// ============================================================================

export interface BatchDraft {
  row_index: number;
  status: string;
  source_file: string;
  source_file_id: string;
  invoice_id: string;
  invoice_date: string;
  seller_name: string;
  seller_id: string;
  buyer_name: string;
  buyer_id: string;
  sale_amount: string;
  tax_amount: string;
  total_amount: string;
  order_id: string;
  document_type: string;
  copy_type: string;
  direction: string;
  confidence: string;
  bbox_json: string;
  page_image_id: string;
  purchase_items: string;
  processed_at: string;
  confirmed_at: string;
  error: string;
}

export interface BatchDraftsResponse {
  drafts: BatchDraft[];
  total: number;
  draft_count: number;
  confirmed_count: number;
  error_count: number;
}

export interface BatchConfirmItem {
  row_index: number;
  edited_metadata: Record<string, unknown>;
  purchase_items: Array<Record<string, string>>;
  direction: string;
}

export interface BatchConfirmResponse {
  confirmed: number;
  errors: string[];
  invoices_created: string[];
}

export async function listBatchDrafts(status?: string): Promise<BatchDraftsResponse> {
  const params = status ? `?status=${status}` : '';
  return get<BatchDraftsResponse>(`${API_BASE}/batch-drafts${params}`);
}

export async function confirmBatchDrafts(confirmations: BatchConfirmItem[], moveProcessed?: boolean): Promise<BatchConfirmResponse> {
  return post<BatchConfirmResponse>(`${API_BASE}/batch-confirm`, {
    confirmations,
    move_processed: moveProcessed ?? false,
  });
}

export async function updateBatchDraft(rowIndex: number, fields: Record<string, string>): Promise<void> {
  await post(`${API_BASE}/batch-draft-update`, { row_index: rowIndex, fields });
}
